import {
  Band,
  band,
  breakpoints,
  Col,
  frosted,
  Icon,
  type LayoutChangeEvent,
  Tappable,
  useDragSize,
  useTheme,
  type ViewStyle,
} from "@rally/ui-kit";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Aura } from "./aura.tsx";
import { columnPad } from "./brand.tsx";
import { say } from "./localization/say.ts";
import { showsBand } from "./surface.ts";
import { Swipe } from "./swipe.tsx";

export type ColumnSize = { width: number; height: number };

const LEAD_KEY = "rally.sidebar.width";
const LEAD_LEAST = 280;
const LEAD_MOST = 480;

function rememberedLead(): number {
  try {
    const said = typeof localStorage === "undefined" ? null : Number(localStorage.getItem(LEAD_KEY));
    return said && said >= LEAD_LEAST && said <= LEAD_MOST ? said : band.lead;
  } catch {
    return band.lead;
  }
}

export function Frame({
  brand,
  list,
  account,
  main,
  details,
  search,
  focused = false,

  at,

  even = false,
}: {
  brand: (size: ColumnSize) => ReactNode;
  list?: ReactNode;
  account?: ReactNode;
  main?: ReactNode;

  details?: ReactNode;

  search?: ReactNode;

  focused?: boolean;
  at?: string | null;
  even?: boolean;
}) {
  const theme = useTheme();
  const [size, setSize] = useState<ColumnSize>({ width: 0, height: 0 });
  const [open, setOpen] = useState(false);
  const measure = (event: LayoutChangeEvent) => {
    const box = event.nativeEvent.layout;
    setSize((was) =>
      was.width === box.width && was.height === box.height ? was : { width: box.width, height: box.height },
    );
  };
  const narrow = theme.isAtMost(breakpoints.phone);
  const lead = useDragSize({
    axis: "x",
    initial: rememberedLead(),
    min: LEAD_LEAST,
    max: LEAD_MOST,
    label: "resize the sidebar",
  });
  useEffect(() => {
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(LEAD_KEY, String(Math.round(lead.size)));
    } catch {}
  }, [lead.size]);

  useEffect(() => {
    if (at !== null) setOpen(false);
  }, [at]);

  const inset = even ? 0 : columnPad(narrow);

  return (
    <Col
      style={{
        flex: 1,
        flexDirection: narrow ? "column" : "row",
        backgroundColor: theme.color["bg.app"],
      }}
    >
      <Aura />

      {narrow ? null : (
        <Col
          testID="frame-column"
          onLayout={measure}
          style={
            {
              paddingHorizontal: inset,
              width: even ? "50%" : lead.size,
              backgroundColor: "transparent",
              zIndex: 40,
              transitionProperty: "width",
              transitionDuration: theme.preferReduced || lead.dragging ? "0ms" : "460ms",
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
            } as ViewStyle
          }
        >
          {even ? (
            <Col style={{ height: 168, backgroundColor: "transparent" }}>{brand(size)}</Col>
          ) : (
            <Col
              style={{
                height: 64,
                zIndex: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "transparent",
              }}
            >
              <Col style={{ flex: 1, minWidth: 0, justifyContent: "center" }}>{brand(size)}</Col>
              {search}
              {account}
            </Col>
          )}
          {list}
          {even ? null : (
            <Col
              {...lead.props}
              testID="sidebar-resize"
              style={{
                ...(lead.props.style as ViewStyle),
                position: "absolute",
                top: 0,
                bottom: 0,
                right: -4,
                width: 8,
                backgroundColor: lead.dragging ? theme.color.accentSoft : "transparent",
              }}
            />
          )}
        </Col>
      )}

      {narrow && even ? (
        <Col testID="frame-column" onLayout={measure} style={{ height: 168, backgroundColor: "transparent" }}>
          {brand(size)}
        </Col>
      ) : null}

      <Col
        testID="main-panel"
        style={{
          flex: 1,
          margin: narrow ? 0 : theme.space[3],
          marginRight: !narrow && details ? 0 : narrow ? 0 : theme.space[3],
          marginLeft: 0,
          marginTop: even ? (narrow ? 0 : theme.space[3]) : narrow ? band.heightNarrow : theme.space[3],
          borderRadius: theme.radius.xl,
          ...(!narrow && details ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {}),
          ...(narrow ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}),
          overflow: "hidden",
          ...theme.elevation.raised,
          ...(frosted(theme, 30) as object),
        }}
      >
        {main}
      </Col>

      {narrow ? null : (
        <Col
          testID="frame-details"
          style={{
            marginTop: theme.space[3],
            flexDirection: "row",
            alignSelf: "stretch",
          }}
        >
          {details}
        </Col>
      )}
      {narrow ? details : null}

      {showsBand(narrow, even, focused) ? (
        <Band
          narrow
          style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 30 }}
          lead={
            <Tappable
              onPress={() => setOpen((was) => !was)}
              accessibilityRole="button"
              accessibilityState={{ expanded: open }}
              accessibilityLabel={say("nav.menu")}
              testID="nav-handle"
              style={{ flex: 1, alignSelf: "stretch", flexDirection: "row", alignItems: "center" }}
            >
              <Col style={{ width: 40, height: 44, alignItems: "center", justifyContent: "center" }}>
                <Icon name="menu" size={20} color="text.secondary" />
              </Col>
              <Col style={{ flex: 1, minWidth: 0 }}>{brand(size)}</Col>
            </Tappable>
          }
        >
          {search}
          {account}
        </Band>
      ) : null}

      {narrow && !even && list && open ? (
        <Swipe onDismiss={() => setOpen(false)} testID="nav-sheet" zIndex={24}>
          {list}
        </Swipe>
      ) : null}
    </Col>
  );
}
